/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 */

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import {HttpStatusCode} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';

import {ApiError} from '../../../models/errors/api-error';

import isEmail from 'validator/lib/isEmail';
import {AccessRequestDto, AccessRole} from '../../../models/access-request.dto';
import {AccessRequestManagementService} from '../../../services/access-request-management.service';
import {ToastService} from '@siemens/ix-angular';

// Form control names that hold plain text and should be trimmed before submit
const TEXT_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'organization',
  'purposeOfAccess',
] as const;

const MAX_LENGTH_SHORT = 100; // firstName, lastName, email, organization
const MAX_LENGTH_LONG = 300; // purposeOfAccess

const I18N_PREFIX = 'battery_pass.public.request_access_form';
const KEY_ERROR_FIELD_REQUIRED = `${I18N_PREFIX}.error_field_required`;
const KEY_ERROR_LEADING_WHITESPACE = `${I18N_PREFIX}.error_leading_whitespace`;
const KEY_ERROR_UNSAFE_INPUT = `${I18N_PREFIX}.error_unsafe_input`;
const KEY_ERROR_NAME_PATTERN = `${I18N_PREFIX}.error_name_pattern`;
const KEY_ERROR_MAX_LENGTH_100 = `${I18N_PREFIX}.error_max_length_100`;
const KEY_ERROR_MAX_LENGTH_300 = `${I18N_PREFIX}.error_max_length_300`;
const KEY_ERROR_EMAIL_FORMAT = `${I18N_PREFIX}.error_email_format`;
const KEY_LABEL_FIRST_NAME = `${I18N_PREFIX}.label_first_name`;
const KEY_LABEL_LAST_NAME = `${I18N_PREFIX}.label_last_name`;
const KEY_LABEL_EMAIL = `${I18N_PREFIX}.label_email`;
const KEY_LABEL_ORGANIZATION = `${I18N_PREFIX}.label_organization`;
const KEY_LABEL_PURPOSE_OF_ACCESS = `${I18N_PREFIX}.label_purpose_of_access`;
const KEY_TOAST_ERROR_CONFLICT = `${I18N_PREFIX}.toast_error_conflict`;
const KEY_TOAST_ERROR_SUBMIT = `${I18N_PREFIX}.toast_error_submit`;

const ROLE_MAP: Record<string, AccessRole> = {
  roleCommission: AccessRole.COMMISSION,
  roleNotifiedBodies: AccessRole.NOTIFIED_BODIES,
  roleMarketSurveillance: AccessRole.MARKET_SURVEILLANCE_AUTHORITIES,
  roleRepair: AccessRole.PERSON_INTEREST_REPAIR,
  roleRecycler: AccessRole.PERSON_INTEREST_RECYCLER,
  roleNatural: AccessRole.PERSON_INTEREST_NATURAL,
  roleTreatmentFacility: AccessRole.PERSON_INTEREST_TREATMENT_FACILITY,
};

const noLeadingWhitespace: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value: string = control.value ?? '';
  return value !== value.trimStart() ? {leadingWhitespace: true} : null;
};

const namePatternValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  // accept trailing whitespace - is removed before submit
  const value: string = (control.value ?? '').trimEnd();
  if (!value) {
    return null;
  }
  // Unicode property escape \p{L} matches any letter in any language
  const valid = /^[\p{L}'\-]+(\s[\p{L}'\-]+)*$/u.test(value);
  return valid ? null : {namePattern: true};
};

/**
 * Allowlist-based XSS guard. Accepts only characters that are legitimate in
 * the form's free-text fields (names, e-mail, organisation, purpose of
 * access). Any character outside the set — including
 * angle brackets, backticks and other injection vectors — is rejected.
 */
const sanitizeInput: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value: string = control.value ?? '';
  // Allows: Unicode letters/numbers, whitespace, and the punctuation
  // characters that appear in real-world values for these fields.
  const allowlist = /^[\p{L}\p{N}\s@.,;:!?'"()\-&+/_#%*=~\\^|]*$/u;
  return value && !allowlist.test(value) ? {unsafeInput: true} : null;
};

const atLeastOneRoleValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const group = control as FormGroup;
  const anyChecked = Object.keys(ROLE_MAP).some(
    (key) => !!group.get(key)?.value,
  );
  return anyChecked ? null : {atLeastOneRole: true};
};

@Component({
  selector: 'app-request-access-form',
  templateUrl: './request-access-form.component.html',
  styleUrl: './request-access-form.component.scss',
  standalone: false,
})
export class RequestAccessFormComponent implements OnInit {
  private readonly accessRequestManagementService = inject(
    AccessRequestManagementService,
  );
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  @Input() passportId!: string;
  @Input() organizationId!: string;
  @Input() size!: string;

  @Output() submitted = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<void>();

  requestAccessForm!: FormGroup;

  hasBeenSubmitted = false;

  get firstName(): AbstractControl {
    return this.getControl('firstName');
  }

  get lastName(): AbstractControl {
    return this.getControl('lastName');
  }

  get email(): AbstractControl {
    return this.getControl('email');
  }

  get organization(): AbstractControl {
    return this.getControl('organization');
  }

  get purposeOfAccess(): AbstractControl {
    return this.getControl('purposeOfAccess');
  }

  private getControl(name: string): AbstractControl {
    const control = this.requestAccessForm.get(name);
    if (!control) {
      throw new Error(`Form control '${name}' not found`);
    }
    return control;
  }

  private getInvalidText(
    ctrl: AbstractControl,
    messages: Partial<Record<string, string>>,
  ): string {
    if (!ctrl.invalid || (!ctrl.touched && !ctrl.dirty)) {
      return '';
    }
    if (
      !ctrl.touched &&
      ctrl.hasError('required') &&
      Object.keys(ctrl.errors ?? {}).length === 1
    ) {
      return '';
    }
    for (const [error, message] of Object.entries(messages)) {
      if (ctrl.hasError(error)) {
        return message ?? '';
      }
    }
    return '';
  }

  get firstNameInvalidText(): string {
    return this.getInvalidText(this.firstName, {
      required: this.translate.instant(KEY_ERROR_FIELD_REQUIRED, {
        fieldLabel: this.translate.instant(KEY_LABEL_FIRST_NAME),
      }),
      leadingWhitespace: this.translate.instant(KEY_ERROR_LEADING_WHITESPACE),
      unsafeInput: this.translate.instant(KEY_ERROR_UNSAFE_INPUT),
      namePattern: this.translate.instant(KEY_ERROR_NAME_PATTERN),
      maxlength: this.translate.instant(KEY_ERROR_MAX_LENGTH_100),
    });
  }

  get lastNameInvalidText(): string {
    return this.getInvalidText(this.lastName, {
      required: this.translate.instant(KEY_ERROR_FIELD_REQUIRED, {
        fieldLabel: this.translate.instant(KEY_LABEL_LAST_NAME),
      }),
      leadingWhitespace: this.translate.instant(KEY_ERROR_LEADING_WHITESPACE),
      unsafeInput: this.translate.instant(KEY_ERROR_UNSAFE_INPUT),
      namePattern: this.translate.instant(KEY_ERROR_NAME_PATTERN),
      maxlength: this.translate.instant(KEY_ERROR_MAX_LENGTH_100),
    });
  }

  get emailInvalidText(): string {
    return this.getInvalidText(this.email, {
      required: this.translate.instant(KEY_ERROR_FIELD_REQUIRED, {
        fieldLabel: this.translate.instant(KEY_LABEL_EMAIL),
      }),
      leadingWhitespace: this.translate.instant(KEY_ERROR_LEADING_WHITESPACE),
      unsafeInput: this.translate.instant(KEY_ERROR_UNSAFE_INPUT),
      email: this.translate.instant(KEY_ERROR_EMAIL_FORMAT),
      maxlength: this.translate.instant(KEY_ERROR_MAX_LENGTH_100),
    });
  }

  get organizationInvalidText(): string {
    return this.getInvalidText(this.organization, {
      required: this.translate.instant(KEY_ERROR_FIELD_REQUIRED, {
        fieldLabel: this.translate.instant(KEY_LABEL_ORGANIZATION),
      }),
      leadingWhitespace: this.translate.instant(KEY_ERROR_LEADING_WHITESPACE),
      maxlength: this.translate.instant(KEY_ERROR_MAX_LENGTH_100),
      unsafeInput: this.translate.instant(KEY_ERROR_UNSAFE_INPUT),
    });
  }

  get purposeOfAccessInvalidText(): string {
    return this.getInvalidText(this.purposeOfAccess, {
      required: this.translate.instant(KEY_ERROR_FIELD_REQUIRED, {
        fieldLabel: this.translate.instant(KEY_LABEL_PURPOSE_OF_ACCESS),
      }),
      leadingWhitespace: this.translate.instant(KEY_ERROR_LEADING_WHITESPACE),
      maxlength: this.translate.instant(KEY_ERROR_MAX_LENGTH_300),
      unsafeInput: this.translate.instant(KEY_ERROR_UNSAFE_INPUT),
    });
  }

  get rolesInvalidText(): string {
    if (
      this.hasBeenSubmitted &&
      this.requestAccessForm.hasError('atLeastOneRole')
    ) {
      return this.translate.instant(
        'battery_pass.public.request_access_form.error_roles',
      );
    }
    return '';
  }

  get hasAnyBlockingError(): boolean {
    return (
      this.firstName.hasError('required') ||
      this.lastName.hasError('required') ||
      this.email.hasError('required') ||
      this.organization.hasError('required') ||
      this.purposeOfAccess.hasError('required') ||
      this.requestAccessForm.hasError('atLeastOneRole')
    );
  }

  ngOnInit(): void {
    this.createForm();
  }

  private createForm(): void {
    const nameValidators = [
      Validators.required,
      noLeadingWhitespace,
      sanitizeInput,
      namePatternValidator,
      Validators.maxLength(MAX_LENGTH_SHORT),
    ];

    this.requestAccessForm = this.formBuilder.group(
      {
        firstName: ['', nameValidators],
        lastName: ['', nameValidators],
        email: [
          '',
          [
            Validators.required,
            noLeadingWhitespace,
            sanitizeInput,
            (control: AbstractControl): ValidationErrors | null => {
              const trimmed: string = (control.value ?? '').trimEnd();
              return trimmed && !isEmail(trimmed) ? {email: true} : null;
            },
            Validators.maxLength(MAX_LENGTH_SHORT),
          ],
        ],
        organization: [
          '',
          [
            Validators.required,
            noLeadingWhitespace,
            sanitizeInput,
            Validators.maxLength(MAX_LENGTH_SHORT),
          ],
        ],
        purposeOfAccess: [
          '',
          [
            Validators.required,
            noLeadingWhitespace,
            sanitizeInput,
            Validators.maxLength(MAX_LENGTH_LONG),
          ],
        ],
        roleCommission: [false],
        roleNotifiedBodies: [false],
        roleMarketSurveillance: [false],
        roleRepair: [false],
        roleRecycler: [false],
        roleNatural: [false],
        roleTreatmentFacility: [false],
      },
      {validators: atLeastOneRoleValidator},
    );
  }

  onSubmit(): void {
    this.hasBeenSubmitted = true;
    this.requestAccessForm.markAllAsTouched();

    if (this.requestAccessForm.invalid) {
      return;
    }

    // Trim trailing whitespace from all text fields before submitting
    TEXT_FIELDS.forEach((field) => {
      const control = this.requestAccessForm.get(field);
      if (control) {
        control.setValue((control.value as string).trimEnd(), {
          emitEvent: false,
        });
      }
    });

    const roles = Object.entries(ROLE_MAP)
      .filter(([key]) => !!this.requestAccessForm.get(key)?.value)
      .map(([, roleString]) => roleString);

    const payload: AccessRequestDto = {
      firstName: this.firstName.value,
      lastName: this.lastName.value,
      email: this.email.value,
      organization: this.organization.value,
      purposeOfAccess: this.purposeOfAccess.value,
      roles: roles,
      passportId: this.passportId,
    };

    if (!this.organizationId) {
      this.toastService.show({
        message: this.translate.instant(
          'battery_pass.public.request_access_form.toast_error_no_org',
        ),
        type: 'error',
      });
      return;
    }

    this.accessRequestManagementService
      .requestExternalAccess$(this.organizationId, payload)
      .subscribe({
        next: () => {
          this.toastService.show({
            message: this.translate.instant(
              'battery_pass.public.request_access_form.toast_success',
            ),
            type: 'success',
          });
          this.submitted.emit(true);
        },
        error: (error: unknown) => {
          const isConflict =
            error instanceof ApiError &&
            error.status === HttpStatusCode.Conflict;
          this.toastService.show({
            message: this.translate.instant(
              isConflict ? KEY_TOAST_ERROR_CONFLICT : KEY_TOAST_ERROR_SUBMIT,
            ),
            type: 'error',
          });
        },
      });
  }
}
